import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
	Alert,
	Box,
	CircularProgress,
	Grid,
	Skeleton,
	TextField,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { Translate } from "@/components/Application/Translate";
import { ManualHeading } from "@/components/Procedures/Checklist/Checklist";
import { ChecklistRow } from "@/components/Procedures/Checklist/ChecklistRow";
import CustomCard from "@/components/Task/CustomCardSuggestion";

import { UserContext } from "@/model/User/UserContext";

import {
	UseDeleteProcedureUpdateSuggestionComment,
	UsePutProcedureUpdateSuggestions,
} from "@/service/ProcedureMutations";
import { useFetchChecklistForTask } from "@/service/ProcedureQueries";
import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";

/* -------------------------------------------------------------------------- */
/* utils                                                                      */
/* -------------------------------------------------------------------------- */

const RIGHT_PANEL_ID = "checklist-detail-scroll";

const toInt = (v: unknown): number | undefined => {
	const n = Number(v);
	return Number.isFinite(n) ? n : undefined;
};

function filterSuggestions(data: any[], page: string, userId?: number) {
	if (!Array.isArray(data)) return [];
	return data
		.map((procedure) => ({
			...procedure,
			Checklists: procedure.Checklists?.map((checklist: any) => ({
				...checklist,
				ProcedureUpdateSuggestions: (
					checklist.ProcedureUpdateSuggestions ?? []
				).filter((s: any) => {
					switch (page) {
						case "newImprov":
							return s.Status === "Unverified";
						case "myImprov":
							return s.CreatedBy?.Id === userId;
						case "verified":
							return s.Status === "Verified";
						case "declined":
							return s.Status === "Declined";
						default:
							return true;
					}
				}),
			})).filter((c: any) => (c.ProcedureUpdateSuggestions?.length ?? 0) > 0),
		}))
		.filter((p) => (p.Checklists?.length ?? 0) > 0);
}

const stripHtml = (s?: string) =>
	s
		? s
				.replace(/<[^>]*>/g, " ")
				.replace(/\s+/g, " ")
				.trim()
		: "";

const truncate = (s: string, n = 80) =>
	s.length > n ? `${s.slice(0, n - 1)}…` : s;

const useHighlight = () => {
	const theme = useTheme();
	return (text?: string, term?: string) => {
		const t = text ?? "";
		const q = (term ?? "").trim().toLowerCase();
		if (!q) return t;
		const idx = t.toLowerCase().indexOf(q);
		if (idx === -1) return t;
		const before = t.slice(0, idx);
		const match = t.slice(idx, idx + q.length);
		const after = t.slice(idx + q.length);
		return (
			<>
				{before}
				<Box
					component="mark"
					sx={{
						px: 0.25,
						borderRadius: 0.5,
						backgroundColor: alpha(theme.palette.warning.main, 0.25),
					}}
				>
					{match}
				</Box>
				{after}
			</>
		);
	};
};

const LabelWithHighlight: React.FC<{
	prefix: string;
	text?: string;
	render: (t?: string) => React.ReactNode;
}> = ({ prefix, text, render }) => (
	<Box
		sx={{
			display: "inline-grid",
			gridTemplateColumns: "auto 1fr",
			columnGap: 1,
			alignItems: "baseline",
			minWidth: 0,
			maxWidth: "100%",
		}}
	>
		<Typography
			component="span"
			variant="caption"
			sx={{ fontWeight: 800, opacity: 0.8 }}
		>
			{prefix}:
		</Typography>
		<Typography
			component="span"
			variant="body2"
			sx={{
				minWidth: 0,
				overflow: "hidden",
				whiteSpace: "nowrap",
				textOverflow: "ellipsis",
			}}
		>
			{render(text)}
		</Typography>
	</Box>
);

/* -------------------------------------------------------------------------- */
/* component                                                                   */
/* -------------------------------------------------------------------------- */

const ProcedureUpdates: React.FC<{
	page: string;
	hasMaterialHandling?: boolean;
}> = ({ page, hasMaterialHandling }) => {
	const user = useContext(UserContext);
	const theme = useTheme();
	const highlight = useHighlight();

	// URL <-> state
	const [searchParams, setSearchParams] = useSearchParams();
	const urlChecklistId = toInt(searchParams.get("checklistId"));
	const urlStepId = toInt(searchParams.get("stepId"));
	const urlSuggestionId = toInt(searchParams.get("suggestionId"));

	// Data
	const {
		data,
		isLoading,
		error,
		refetch: refetchSuggestions,
	} = useFetchProcedureUpdateSuggestions(!!hasMaterialHandling);

	// Local state
	const [filteredData, setFilteredData] = useState<any[]>([]);
	const [searchTerm, setSearchTerm] = useState("");

	// Selection
	const [selectedChecklist, setSelectedChecklist] = useState<any | null>(null);
	const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(
		null,
	);
	const [selectedSuggestionStepId, setSelectedSuggestionStepId] = useState<
		number | null
	>(null);
	const [selectedSuggestion, setSelectedSuggestion] = useState<any | null>(
		null,
	);

	// Mode: keep “interactive”
	const [isInteractiveMode] = useState(true);

	// Checklist details
	const { data: ChecklistData, isLoading: isLoadingChecklist } =
		useFetchChecklistForTask(
			selectedChecklist?.ProcedureId,
			selectedChecklistId || undefined,
		);

	// Mutations
	const putProcedureUpdateSuggestions = UsePutProcedureUpdateSuggestions();
	const deleteCommentMutation = UseDeleteProcedureUpdateSuggestionComment();

	/* ---------------------------- derive quick indexes ---------------------------- */
	const index = useMemo(() => {
		const checklistById = new Map<number, any>();
		const suggestionById = new Map<number, any>();
		const checklistIdBySuggestionId = new Map<number, number>();

		filteredData.forEach((proc: any) => {
			proc.Checklists?.forEach((cl: any) => {
				checklistById.set(cl.ChecklistId, {
					...cl,
					ProcedureId: proc.ProcedureId,
				});
				cl.ProcedureUpdateSuggestions?.forEach((s: any) => {
					suggestionById.set(s.Id, s);
					checklistIdBySuggestionId.set(s.Id, cl.ChecklistId);
				});
			});
		});

		return { checklistById, suggestionById, checklistIdBySuggestionId };
	}, [filteredData]);

	/* ---------------------------- filter incoming data ---------------------------- */
	useEffect(() => {
		if (!data) return;
		setFilteredData(filterSuggestions(data, page, user?.Id));
	}, [data, page, user?.Id]);

	/* ------------------------ reset selection on page switch ----------------------- */
	useEffect(() => {
		setSelectedChecklist(null);
		setSelectedChecklistId(null);
		setSelectedSuggestion(null);
		setSelectedSuggestionStepId(null);
	}, [page]);

	/* ------------------------ restore selection: URL -> LS -> none ------------------------ */
	useEffect(() => {
		// URL first
		if (urlChecklistId || urlSuggestionId || urlStepId) {
			const checklistId =
				urlChecklistId ??
				(urlSuggestionId
					? index.checklistIdBySuggestionId.get(urlSuggestionId)
					: undefined);

			if (checklistId) {
				const cl = index.checklistById.get(checklistId);
				if (cl) {
					setSelectedChecklist(cl);
					setSelectedChecklistId(checklistId);

					const sug =
						(urlSuggestionId && index.suggestionById.get(urlSuggestionId)) ??
						cl.ProcedureUpdateSuggestions?.find(
							(s: any) => s.StepId === urlStepId,
						);

					if (sug) {
						setSelectedSuggestion(sug);
						setSelectedSuggestionStepId(sug.StepId);
					} else if (urlStepId) {
						setSelectedSuggestionStepId(urlStepId);
					}
					return;
				}
			}
		}

		// Local storage fallback
		const saved = JSON.parse(
			localStorage.getItem(`suggestionSelection_${page}`) || "{}",
		);
		if (saved.checklistId) {
			const cl = index.checklistById.get(saved.checklistId);
			if (cl) {
				setSelectedChecklist(cl);
				setSelectedChecklistId(saved.checklistId);
				if (saved.stepId) {
					setSelectedSuggestionStepId(saved.stepId);
					const sug = cl.ProcedureUpdateSuggestions?.find(
						(s: any) => s.StepId === saved.stepId,
					);
					setSelectedSuggestion(sug || null);
				}
			}
		}
	}, [urlChecklistId, urlSuggestionId, urlStepId, index, page]);

	/* ------------------------------ url sync helper ------------------------------ */
	const updateUrlSelection = (sel: {
		checklistId?: number | null;
		stepId?: number | null;
		suggestionId?: number | null;
	}) => {
		const params = new URLSearchParams(searchParams);
		const setOrDelete = (k: string, v?: number | null) => {
			if (v == null || Number.isNaN(v)) params.delete(k);
			else params.set(k, String(v));
		};
		setOrDelete("checklistId", sel.checklistId);
		setOrDelete("stepId", sel.stepId);
		setOrDelete("suggestionId", sel.suggestionId);
		setSearchParams(params, { replace: true });

		localStorage.setItem(
			`suggestionSelection_${page}`,
			JSON.stringify({
				checklistId: sel.checklistId ?? selectedChecklistId ?? undefined,
				stepId: sel.stepId ?? selectedSuggestionStepId ?? undefined,
				suggestionId: sel.suggestionId ?? selectedSuggestion?.Id ?? undefined,
			}),
		);
	};

	/* --------------------------------- refresh ---------------------------------- */
	const refreshSuggestions = async () => {
		const updated = await refetchSuggestions({ force: true });
		const filtered = filterSuggestions(updated.data, page, user?.Id);
		setFilteredData(filtered);
		updateUrlSelection({
			checklistId: urlChecklistId ?? selectedChecklistId,
			stepId: urlStepId ?? selectedSuggestionStepId,
			suggestionId: urlSuggestionId ?? selectedSuggestion?.Id ?? undefined,
		});
	};

	/* --------------------------------- handlers --------------------------------- */
	const handleTreeItemClick = (
		checklistOrSuggestion: any,
		isSuggestion = false,
	) => {
		if (isSuggestion) {
			const { checklist, suggestion } = checklistOrSuggestion;
			setSelectedChecklist({
				...checklist,
				ProcedureId: checklist.ProcedureId,
			});
			setSelectedChecklistId(checklist.ChecklistId);
			setSelectedSuggestion(suggestion);
			setSelectedSuggestionStepId(suggestion.StepId);
			updateUrlSelection({
				checklistId: checklist.ChecklistId,
				stepId: suggestion.StepId,
				suggestionId: suggestion.Id,
			});
			return;
		}
		setSelectedChecklist(checklistOrSuggestion);
		setSelectedChecklistId(checklistOrSuggestion.ChecklistId);
		setSelectedSuggestion(null);
		setSelectedSuggestionStepId(null);
		updateUrlSelection({
			checklistId: checklistOrSuggestion.ChecklistId,
			stepId: null,
			suggestionId: null,
		});
	};

	const handleStepIdSelected = (stepId: number) => {
		setSelectedSuggestionStepId(stepId);
		const existing = JSON.parse(
			localStorage.getItem(`suggestionSelection_${page}`) || "{}",
		);
		localStorage.setItem(
			`suggestionSelection_${page}`,
			JSON.stringify({ ...existing, stepId }),
		);
		// scrolling is handled inside Step via scrollContainerId, no window jump
	};

	const handleUpdateStatusForCard = async (
		suggestionId: number,
		newStatus: string,
		otherDetails: any,
	) => {
		if (!suggestionId || !otherDetails) return;
		const request = {
			ProcedureId: otherDetails.procedureId,
			StepId: otherDetails.stepId,
			Description: otherDetails.description,
			Anonymous: otherDetails.anonymous,
			Attachment: otherDetails.attachment || null,
			Status: newStatus,
			DeclinedComment: otherDetails.declinedComment || "",
		};
		try {
			await putProcedureUpdateSuggestions({ id: suggestionId, request });
			await refreshSuggestions();
		} catch (e) {
			console.error("Error updating status", e);
		}
	};

	const handleDeleteComment = async (commentId: number) => {
		try {
			await deleteCommentMutation.mutateAsync(commentId);
			await refreshSuggestions();
		} catch (e) {
			console.error("Failed to delete comment", e);
		}
	};

	/* ---------------------------------- render ---------------------------------- */

	return (
		<Box
			sx={{
				height: "100vh", // lock page to viewport
				overflow: "hidden", // prevent window scroll
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Grid
				container
				spacing={2}
				sx={{
					p: 2,
					flex: 1,
					minHeight: 0, // allow children to define their own scroll areas
				}}
			>
				{/* LEFT: Procedures / Checklists tree */}
				<Grid item xs={12} sm={3} md={3} sx={{ height: "100%", minHeight: 0 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<TextField
							label={<Translate String="ProcedureUpd.Search">Search</Translate>}
							fullWidth
							size="small"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</Box>

					<Box sx={{ mt: 1.5, height: "calc(100% - 44px)", overflowY: "auto" }}>
						<SimpleTreeView>
							{filteredData
								?.filter((p) =>
									searchTerm.trim()
										? String(p.ProcedureName)
												.toLowerCase()
												.includes(searchTerm.toLowerCase())
										: true,
								)
								.map((procedure) => (
									<TreeItem
										key={procedure.ProcedureId}
										itemId={String(procedure.ProcedureId)}
										label={
											<LabelWithHighlight
												prefix="Procedure"
												text={procedure.ProcedureName}
												render={(t) => highlight(t, searchTerm)}
											/>
										}
									>
										{procedure.Checklists?.map((checklist: any) => (
											<TreeItem
												key={checklist.ChecklistId}
												itemId={String(checklist.ChecklistId)}
												label={
													<LabelWithHighlight
														prefix="Checklist"
														text={checklist.ChecklistName}
														render={(t) => highlight(t, searchTerm)}
													/>
												}
												onClick={() =>
													handleTreeItemClick({
														...checklist,
														ProcedureId: procedure.ProcedureId,
													})
												}
											>
												{!isInteractiveMode &&
													checklist.ProcedureUpdateSuggestions?.map(
														(s: any) => {
															const clean = truncate(
																stripHtml(s.Description) ||
																	`Suggestion #${s.Id}`,
																90,
															);
															return (
																<TreeItem
																	key={s.Id}
																	itemId={`suggestion-${s.Id}`}
																	label={
																		<LabelWithHighlight
																			prefix="Suggestion"
																			text={clean}
																			render={(t) => highlight(t, searchTerm)}
																		/>
																	}
																	onClick={() =>
																		handleTreeItemClick(
																			{
																				checklist: {
																					...checklist,
																					ProcedureId: procedure.ProcedureId,
																				},
																				suggestion: s,
																			},
																			true,
																		)
																	}
																/>
															);
														},
													)}
											</TreeItem>
										))}
									</TreeItem>
								))}
						</SimpleTreeView>
					</Box>
				</Grid>

				{/* MIDDLE: Suggestions list */}
				<Grid item xs={12} sm={3} md={3} sx={{ height: "100%", minHeight: 0 }}>
					<Box sx={{ height: "100%", overflowY: "auto" }}>
						{isLoading ? (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								height="100%"
							>
								<CircularProgress />
							</Box>
						) : error ? (
							<Alert severity="error">Error loading suggestions</Alert>
						) : !selectedChecklist ? (
							<Alert severity="info">Please select a checklist</Alert>
						) : isInteractiveMode ? (
							<>
								<Typography variant="h6" gutterBottom>
									{selectedChecklist.ChecklistName}
								</Typography>
								{(selectedChecklist.ProcedureUpdateSuggestions ?? []).map(
									(s: any) => (
										<Box key={s.Id} sx={{ mb: 1.5 }}>
											<CustomCard
												selectedChecklistProcedureId={
													selectedChecklist?.ProcedureId
												}
												selectedChecklistName={selectedChecklist?.ChecklistName}
												suggestion={s}
												selectedCard={selectedSuggestionStepId === s.StepId}
												handleStepIdSelected={handleStepIdSelected}
												handleUpdateStatusForCard={handleUpdateStatusForCard}
												handleDeleteComment={handleDeleteComment}
												refreshSuggestions={refreshSuggestions}
												page={page}
												onOpenSuggestion={(sugg: any) => {
													updateUrlSelection({
														checklistId: selectedChecklistId,
														stepId: sugg.StepId,
														suggestionId: sugg.Id,
													});
												}}
											/>
										</Box>
									),
								)}
							</>
						) : selectedSuggestion ? (
							<CustomCard
								suggestion={selectedSuggestion}
								selectedCard
								handleStepIdSelected={handleStepIdSelected}
								handleUpdateStatusForCard={handleUpdateStatusForCard}
								handleDeleteComment={handleDeleteComment}
								refreshSuggestions={refreshSuggestions}
								page={page}
								onOpenSuggestion={(sugg: any) => {
									updateUrlSelection({
										checklistId: selectedChecklistId,
										stepId: sugg.StepId,
										suggestionId: sugg.Id,
									});
								}}
							/>
						) : (
							<Alert severity="info">Please select a suggestion</Alert>
						)}
					</Box>
				</Grid>

				{/* RIGHT: Checklist detail */}
				{selectedChecklist && (
					<Grid
						item
						xs={12}
						sm={6}
						md={6}
						sx={{ height: "100%", minHeight: 0 }}
					>
						<Box id={RIGHT_PANEL_ID} sx={{ height: "100%", overflowY: "auto" }}>
							{isLoadingChecklist ? (
								[...Array(5)].map((_, i) => (
									<Box key={i} sx={{ p: 1 }}>
										<Skeleton variant="text" width="90%" height={30} />
										<Skeleton variant="text" width="60%" height={30} />
									</Box>
								))
							) : (ChecklistData?.Steps?.length ?? 0) > 0 ? (
								<>
									<Typography variant="h6" sx={{ mt: 0.5 }}>
										{ChecklistData?.Name}
									</Typography>

									{/* Optional ingress */}
									{ChecklistData?.Ingress && (
										<Box sx={{ mt: 1 }}>
											<SATOSContentRender content={ChecklistData.Ingress} />
										</Box>
									)}
									<section className="procedure-wrapper">
										<div className="procedure-wrapper-inner">
											<div className="grid-wrapper">
												<ManualHeading activeSchemeId={selectedChecklistId!} />
												{ChecklistData?.Steps?.map((step: any) => (
													<ChecklistRow
														key={step.Id}
														activeSchemeId={selectedChecklistId!}
														step={step}
														showCommentsAndSuggestion={false}
														highlightedStepId={
															selectedSuggestionStepId ?? undefined
														}
														scrollContainerId={RIGHT_PANEL_ID}
													/>
												))}
											</div>
										</div>
									</section>
								</>
							) : (
								<Alert severity="info">
									No steps found for this checklist.
								</Alert>
							)}
						</Box>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};

export default ProcedureUpdates;
